// Core
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Redux
import { useGetLossesByYearPaginationQuery } from "state/erse/lossesApiSlice";

// Components
import { FieldSearchSelect, RemoteTable } from "components/AtomsV2";

// Utils
import { voltageLevelTypes } from "utils/constants/index";
import { TIME_QUARTERS } from "utils/constants/time";
import { datetimeToTime, formatDate } from "utils/time";

const allowedVoltageLevels = ["BT", "MT", "AT", "AT/RNT", "MAT"];

const value4 = (value) => (
  <span className="tabular-nums" style={{ color: "var(--accent)", fontWeight: 600 }}>
    {value != null && !Number.isNaN(Number(value)) ? Number(value).toFixed(4) : "-"}
  </span>
);

function LossesTable({ validity: validityProp, setIsLoading }) {
  const { validity: routeValidity } = useParams();
  const validity = validityProp ?? routeValidity;
  const limit = 10;
  const [sortOrder, setSortOrder] = useState(undefined);
  const [page, setPage] = useState(0);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [voltageLevel, setVoltageLevel] = useState(null);

  const { t } = useTranslation(["erse"]);

  const {
    data: responseDict,
    isLoading,
    isFetching,
  } = useGetLossesByYearPaginationQuery(
    {
      year: validity,
      limit,
      page,
      date,
      time,
      voltageLevel,
      sortColumn: sortOrder?.column,
      sortDirection: sortOrder?.direction,
    },
    {
      pollingInterval: 60000 * 60 * 24,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: !validity,
    }
  );

  useEffect(() => {
    setIsLoading(isLoading || isFetching);
  }, [isLoading, isFetching, setIsLoading]);

  useEffect(() => {
    setPage(0);
    setDate(null);
    setTime(null);
    setVoltageLevel(null);
    setSortOrder(undefined);
  }, [validity]);

  const rows = useMemo(
    () =>
      responseDict?.data?.map((loss, index) => ({
        id: `${loss.datetime_timestamp}-${loss.voltage_level}-${index}`,
        date: formatDate(loss.datetime_timestamp),
        time: datetimeToTime(loss.datetime_timestamp),
        voltage_level: loss.voltage_level,
        value: loss.value,
      })) ?? [],
    [responseDict]
  );

  const columns = [
    {
      key: "date",
      label: t("tables.losses.columns.date"),
      sortable: true,
      width: "20%",
    },
    {
      key: "time",
      label: t("tables.losses.columns.time"),
      sortable: true,
      width: "20%",
    },
    {
      key: "voltage_level",
      label: t("tables.losses.columns.voltage_level"),
      sortable: true,
      width: "24%",
      render: (value) => <span className="font-medium text-ink">{value}</span>,
    },
    {
      key: "value",
      label: t("tables.losses.columns.value"),
      align: "right",
      sortable: true,
      render: value4,
    },
  ];

  const dateOptions = [
    { value: "", label: t("tables.losses.filters.all_dates") },
    ...(responseDict?.distinct_dates ?? []).map((obj) => ({
      value: obj.date,
      label: formatDate(obj.date),
    })),
  ];
  const timeOptions = [
    { value: "", label: t("tables.losses.filters.all_times") },
    ...((responseDict?.distinct_times ?? []).length
      ? responseDict.distinct_times.map((obj) => ({ value: obj.time, label: obj.time }))
      : TIME_QUARTERS.map((timeQuarter) => ({ value: timeQuarter, label: timeQuarter }))),
  ];
  const voltageLevelOptions = [
    { value: "", label: t("tables.losses.filters.all_voltage_levels") },
    ...voltageLevelTypes
      .map((obj) => obj.label)
      .filter((label) => allowedVoltageLevels.includes(label))
      .map((label) => ({ value: label, label })),
  ];

  const onFilter = (setter) => (value) => {
    setPage(0);
    setter(value || null);
  };

  const filters = (
    <>
      <FieldSearchSelect
        label={t("tables.losses.columns.date")}
        value={date ?? ""}
        onChange={onFilter(setDate)}
        options={dateOptions}
      />
      <FieldSearchSelect
        label={t("tables.losses.columns.time")}
        value={time ?? ""}
        onChange={onFilter(setTime)}
        options={timeOptions}
      />
      <FieldSearchSelect
        label={t("tables.losses.columns.voltage_level")}
        value={voltageLevel ?? ""}
        onChange={onFilter(setVoltageLevel)}
        options={voltageLevelOptions}
      />
    </>
  );

  return (
    <RemoteTable
      columns={columns}
      rows={rows}
      totalCount={responseDict?.total_count ?? 0}
      page={page}
      limit={limit}
      onPageChange={setPage}
      sort={sortOrder}
      onSortChange={setSortOrder}
      filters={filters}
      loading={isFetching}
      emptyText={t("list.empty")}
    />
  );
}

LossesTable.defaultProps = {
  validity: null,
  setIsLoading: () => {},
};

LossesTable.propTypes = {
  validity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setIsLoading: PropTypes.func,
};

export default LossesTable;
